import React, { useMemo } from 'react';

import { FiltersValues, SetFiltersValues } from '../../types';
import { useAuth } from 'components/Auth/AuthProvider';

import MultiSelect from 'components/MultiSelect';

import { ANY } from '../../../../constants/building/availableCondos';

type KeyVal = { [index: string]: keyof FiltersValues };

export enum PageType {
  building = 'building',
  search = 'search',
}

const checkboxes: KeyVal = {
  'Show Pending Listings': 'showPendingListings',
};
const searchCheckboxes: KeyVal = {
  'Accepts Bitcoin': 'bitcoin',
  'Seller Financing': 'sellerFinancing',
  'Allows Short Term Rentals': 'shortTermRent',
  'Low Down Payment': 'lowDownPayment',
};

interface Props {
  values: FiltersValues;
  setValues: SetFiltersValues;
  page: PageType;
}

// Agent/admin-only filters. "Avoid Short Term Rentals" stays here until the
// public definition of "short term" is agreed (CBBRR-4275) — for agents it is
// simply the inverse of building.allowsShortTerm.
const adminCheckboxes: KeyVal = {
  'Short Term Rentals': 'shortTermRentTerms',
  'Avoid Short Term Rentals': 'notShortTermRent',
  // Hidden from the UI for now — not in use yet. Logic (values, mapping,
  // getShortTermRentToggleValues) stays intact; re-add this line to show it again.
  // 'Avoid Short Term Rentals At Least': 'notShortTermRentTerms',
  'Rental Investments': 'rentalInvestments',
};

const SHORT_TERM_RENT_KEY = 'shortTermRent';
const NOT_SHORT_TERM_RENT_KEY = 'notShortTermRent';

const RENT_TERM_OPTIONS = [
  { label: 'Any', value: ANY },
  { label: '2 times/year', value: '2' },
  { label: '3-6 times/year', value: '3-6' },
  { label: '12 times/year', value: '12' },
  { label: '365 times/year', value: '365' },
];

type RentTermsField = 'shortTermRentTerms' | 'notShortTermRentTerms';

/**
 * The two frequency sub-filters render as a MultiSelect instead of a checkbox,
 * and only while their parent checkbox is on — keyed by the label used in
 * adminCheckboxes.
 */
const rentTermsSubFilters: Record<
  string,
  { field: RentTermsField; parent: keyof FiltersValues; label: string }
> = {
  'Short Term Rentals': {
    field: 'shortTermRentTerms',
    parent: SHORT_TERM_RENT_KEY,
    label: 'Allow Short Term Rentals At Least',
  },
  'Avoid Short Term Rentals At Least': {
    field: 'notShortTermRentTerms',
    parent: NOT_SHORT_TERM_RENT_KEY,
    label: 'Avoid Short Term Rentals At Least',
  },
};

// shortTermRent and notShortTermRent are opposite views of the same
// building.allowsShortTerm field — checking one must clear the other, plus the
// other's frequency sub-filter, or the pair would combine into a filter that can
// never match anything.
export function getShortTermRentToggleValues(
  key: keyof FiltersValues,
  checked: boolean
): Partial<FiltersValues> | null {
  if (!checked) return null;
  if (key === SHORT_TERM_RENT_KEY) {
    return {
      shortTermRent: true,
      notShortTermRent: false,
      notShortTermRentTerms: [],
    };
  }
  if (key === NOT_SHORT_TERM_RENT_KEY) {
    return {
      notShortTermRent: true,
      shortTermRent: false,
      shortTermRentTerms: [],
    };
  }
  return null;
}

/**
 * Which checkboxes the Special Terms block renders. The building page shows only
 * the shared ones; the search page adds the public filters, and admins/agents
 * additionally get adminCheckboxes.
 */
export function getSpecialTermsCheckboxes(
  page: PageType,
  isAdmin: boolean | undefined
): KeyVal {
  if (page !== PageType.search) return checkboxes;

  return {
    ...checkboxes,
    ...searchCheckboxes,
    ...(isAdmin ? adminCheckboxes : {}),
  };
}

const SpecialTerms: React.FC<Props> = ({ values, setValues, page }) => {
  const { me } = useAuth();
  const { forSale, forRent, sold } = values;
  const checkboxesList = useMemo(
    () => getSpecialTermsCheckboxes(page, me?.isAdmin),
    [me?.isAdmin, page]
  );

  const onSelectChange = (field: RentTermsField, terms: string[]) => {
    setValues((values) => ({
      ...values,
      [field]: terms,
    }));
  };

  const show = forSale || (sold && !forRent);

  // Frequency sub-filters render last so they never split the checkbox grid.
  const orderedKeys = useMemo(() => {
    const keys = Object.keys(checkboxesList);

    return [
      ...keys.filter((key) => !rentTermsSubFilters[key]),
      ...keys.filter((key) => rentTermsSubFilters[key]),
    ];
  }, [checkboxesList]);

  return (
    <>
      {show && (
        <div className="w-full mb-6 pt-6 md:w-2/3 border-beige-dark border-t">
          <div className="md:text-14-26-0.3">Special terms:</div>
          <div className="grid grid-cols-1 md:grid-cols-2">
            {orderedKeys.map((key) =>
              rentTermsSubFilters[key] && me?.isAdmin ? (
                !!values[rentTermsSubFilters[key].parent] && (
                  <div
                    key={key}
                    className="flex flex-col my-2 text-14-26-0.3 md:col-span-2"
                  >
                    <div className="morefilters w-full md:w-[298px]">
                      <span
                        id={`${rentTermsSubFilters[key].field}Label`}
                        className="md:text-14-26-0.3"
                      >
                        {rentTermsSubFilters[key].label}
                      </span>
                      <div
                        className="flex align-center leading-[21px]"
                        role="group"
                        aria-labelledby={`${rentTermsSubFilters[key].field}Label`}
                      >
                        <MultiSelect
                          options={RENT_TERM_OPTIONS}
                          value={values[rentTermsSubFilters[key].field]}
                          onSelectChange={(terms: string[]) =>
                            onSelectChange(
                              rentTermsSubFilters[key].field,
                              terms
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div
                  key={key}
                  className="flex items-center mt-[12px] md:mt-[8px] mb-[4px] text-16-26-0.3 md:text-14-26-0.3"
                >
                  <input
                    id={checkboxesList[key]}
                    checked={!!values[checkboxesList[key]]}
                    onChange={({ target }) =>
                      setValues((values) => ({
                        ...values,
                        [checkboxesList[key]]: target.checked,
                        ...getShortTermRentToggleValues(
                          checkboxesList[key],
                          target.checked
                        ),
                      }))
                    }
                    type="checkbox"
                    className="mr-3 input-checkbox custom-checkbox whitespace-nowrap"
                  />
                  <label
                    className="whitespace-nowrap"
                    htmlFor={checkboxesList[key]}
                  >
                    {key}
                  </label>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SpecialTerms;
